import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, TrendingUp, Shield, Sparkles, Contact2, Building2, ChevronDown, CreditCard, Wallet, QrCode, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';
import MarketingNav from '@/components/MarketingNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Homepage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setIsAuthenticated(true);
    } catch (_e) {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const cta = () => navigate(isAuthenticated ? '/dashboard' : '/signup');

  const steps = [
    {
      step: '01',
      kicker: 'Create',
      title: 'Your profile, elevated.',
      body: 'Build a mini-website in your pocket. Photos, videos, PDFs, links — every introduction becomes memorable.',
      features: ['Edit anytime', 'Brand colours & logo', 'Interactive blocks', 'Multiple contact methods'],
    },
    {
      step: '02',
      kicker: 'Share',
      title: 'One tap. Instant impression.',
      body: 'Tap our NFC smart card, share your QR, drop a link, or add to Apple / Google Wallet — no app required.',
      features: ['NFC smart card', 'Profile QR code', 'Wallet compatible', 'Custom short link'],
    },
    {
      step: '03',
      kicker: 'Capture',
      title: 'Turn conversations into leads.',
      body: 'Every prospect can exchange contacts with you in seconds. Track every view and follow up smarter.',
      features: ['Instant vCard save', 'Two-way lead capture', 'Real-time analytics', 'Trees saved counter'],
    },
  ];

  const featureRow = [
    { icon: Sparkles, title: 'Interactive Blocks', body: 'Gallery, videos, PDFs — build a story worth remembering.' },
    { icon: Zap, title: 'Instant NFC Share', body: 'Tap to share on any modern iPhone or Android. Zero friction.' },
    { icon: TrendingUp, title: 'Analytics', body: 'Views, leads and top-performers in a single beautiful dashboard.' },
    { icon: Contact2, title: 'iOS-safe vCard', body: 'Save-to-contacts works flawlessly on iPhone, Android and everything else.' },
    { icon: Shield, title: 'Own your data', body: 'You control what shows publicly. Delete leads anytime.' },
    { icon: Building2, title: 'For Teams', body: 'Roll out branded cards for every employee from one admin dashboard.' },
  ];

  const shareMethods = [
    { icon: CreditCard, label: 'NFC Smart Card' },
    { icon: QrCode, label: 'Profile QR' },
    { icon: Wallet, label: 'Apple / Google Wallet' },
    { icon: LinkIcon, label: 'Custom Short Link' },
  ];

  const faqs = [
    {
      q: 'What phones is the NFC card compatible with?',
      a: 'iPhones from 2018+ have NFC always-on by default — just tap the card to your prospect\'s phone. Android phones need NFC toggled on, then hover the card on the back. Any phone can also scan the QR on the back.',
    },
    {
      q: 'Can I edit my card after I\'ve shared it?',
      a: 'Yes. Your card is a live URL — update your name, title, socials, gallery or PDFs anytime and every previously-shared card instantly reflects the change.',
    },
    {
      q: 'Do people need to install an app?',
      a: 'No. Your profile opens in the browser instantly. Save contact, capture lead, download vCard — all work without any app on either side.',
    },
    {
      q: 'How does the subscription work?',
      a: 'Your first year is included with the physical card. After that it\'s a low annual fee to keep your public profile live and analytics running.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#0A0A0A]" data-testid="homepage">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 lg:pt-40 pb-16 lg:pb-24 px-6" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A0A0A]/5 border border-[#0A0A0A]/10 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#B8935A]"></span>
            <span className="text-xs font-medium text-[#0A0A0A]">The Digital Business Card, refined.</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-heading font-bold tracking-tight text-[#0A0A0A] leading-[0.95] mb-6">
            Don't just exchange<br />
            contacts—<span className="text-[#B8935A]">build strong</span><br />
            connections.
          </h1>
          <p className="text-lg lg:text-xl text-[#525252] max-w-2xl mb-10 leading-relaxed">
            Sol Digital turns your business card into a mini-website. Share with a tap, capture leads in
            seconds, and follow up with real insight. No apps. No reprints. Ever.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={cta} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-14 px-8 text-base font-semibold shadow-xl shadow-black/10" data-testid="hero-cta">
              Get your card <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button onClick={() => navigate('/pricing')} variant="outline" className="rounded-full h-14 px-8 text-base font-semibold border-[#0A0A0A]/20 bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A]/5" data-testid="hero-secondary">
              See pricing
            </Button>
          </div>

          {/* Product mockup */}
          <div className="mt-20 relative">
            <div className="relative max-w-4xl mx-auto">
              <div className="aspect-[16/10] rounded-3xl bg-gradient-to-br from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A] p-1 shadow-2xl shadow-black/30">
                <div className="w-full h-full rounded-[calc(1.5rem-2px)] bg-[#0A0A0A] flex items-center justify-center relative overflow-hidden">
                  {/* Faux card */}
                  <div className="relative w-[260px] sm:w-[340px] aspect-[1.6/1] rounded-2xl bg-gradient-to-br from-[#1a1a1a] via-[#0A0A0A] to-[#000] border border-[#B8935A]/30 shadow-2xl transform -rotate-6 hover:rotate-0 transition-transform duration-700">
                    <div className="absolute inset-0 p-6 flex flex-col justify-between">
                      <div className="flex items-start justify-between">
                        <div className="w-8 h-8 rounded-full bg-[#B8935A] flex items-center justify-center">
                          <span className="text-black font-bold text-xs">S</span>
                        </div>
                        <div className="w-6 h-6 border border-[#B8935A]/60 rounded" />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#B8935A] mb-1">Sol Digital</p>
                        <p className="text-white text-lg font-heading font-bold tracking-tight">YOUR NAME</p>
                        <p className="text-[10px] text-white/60 mt-0.5">tap to share</p>
                      </div>
                    </div>
                  </div>
                  {/* Ambient glow */}
                  <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#B8935A]/10 blur-3xl" />
                  <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#B8935A]/5 blur-3xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-10 border-y border-[#0A0A0A]/10 bg-[#EBE6DC]/50">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-[#525252] text-center mb-6">Trusted by teams building meaningful connections</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 opacity-70">
            {['Zephyr', 'Northwind', 'Meridian', 'Vertex', 'Halcyon', 'Kestrel'].map((n) => (
              <span key={n} className="text-lg font-heading font-bold tracking-tight text-[#0A0A0A]/60">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Steps 01 02 03 */}
      {steps.map((s, idx) => (
        <section key={s.step} className={`py-20 lg:py-28 px-6 ${idx % 2 === 1 ? 'bg-[#EBE6DC]/40' : ''}`} data-testid={`step-section-${s.step}`}>
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Step {s.step}</p>
                <p className="text-sm font-semibold text-[#525252] mb-3">{s.kicker}</p>
                <h2 className="text-4xl lg:text-6xl font-heading font-bold tracking-tight text-[#0A0A0A] mb-6 leading-[0.95]">
                  {s.title}
                </h2>
                <p className="text-lg text-[#525252] leading-relaxed mb-8 max-w-lg">{s.body}</p>
                <ul className="space-y-3 mb-8">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-[#0A0A0A]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B8935A]" />
                      <span className="font-medium">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={cta} variant="ghost" className="text-[#0A0A0A] font-semibold hover:bg-[#0A0A0A]/5 rounded-full h-11 px-5 -ml-5" data-testid={`step-cta-${s.step}`}>
                  Try Sol Digital <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <StepIllustration idx={idx} />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Feature grid */}
      <section className="py-20 lg:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Everything you need</p>
            <h2 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight text-[#0A0A0A] leading-tight">
              A complete networking toolkit — engineered for people who show up.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featureRow.map((f) => (
              <div key={f.title} className="bg-white border border-[#0A0A0A]/10 rounded-2xl p-8 hover:border-[#B8935A]/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] flex items-center justify-center mb-6">
                  <f.icon className="w-5 h-5 text-[#B8935A]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-2">{f.title}</h3>
                <p className="text-[#525252] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Share methods strip */}
      <section className="py-16 px-6 bg-[#0A0A0A] text-white">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Amplify your reach</p>
          <h2 className="text-3xl lg:text-5xl font-heading font-bold tracking-tight mb-10 leading-tight">
            Share your profile anywhere.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {shareMethods.map((m) => (
              <div key={m.label} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                <m.icon className="w-6 h-6 text-[#B8935A] mb-3 mx-auto" />
                <p className="font-medium text-sm">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teams CTA */}
      <section className="py-20 lg:py-28 px-6 bg-[#EBE6DC]/40">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Sol for Teams</p>
            <h2 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight text-[#0A0A0A] leading-tight mb-6">
              One brand.<br />Every employee.
            </h2>
            <p className="text-lg text-[#525252] leading-relaxed mb-8 max-w-lg">
              Roll out branded digital cards across your entire team from a single admin dashboard. Volume
              pricing starts at ₹100/card/year.
            </p>
            <Link to="/corporate">
              <Button className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-12 px-6 font-semibold" data-testid="teams-cta">
                Explore Sol for Teams <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              'Bulk employee management',
              'Central branding control',
              'Company-wide analytics',
              'Volume discounts',
            ].map((f) => (
              <div key={f} className="bg-white rounded-2xl p-5 border border-[#0A0A0A]/10">
                <div className="w-8 h-8 rounded-lg bg-[#B8935A]/15 flex items-center justify-center mb-3">
                  <span className="w-2 h-2 rounded-full bg-[#B8935A]" />
                </div>
                <p className="font-medium text-[#0A0A0A]">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3 text-center">Questions</p>
          <h2 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight text-[#0A0A0A] text-center mb-12 leading-tight">
            Frequently asked.
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <button
                key={i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left bg-white border border-[#0A0A0A]/10 rounded-2xl overflow-hidden hover:border-[#0A0A0A]/30 transition-colors"
                data-testid={`faq-${i}`}
              >
                <div className="flex items-center justify-between p-6">
                  <p className="font-heading font-bold text-lg text-[#0A0A0A] pr-4">{f.q}</p>
                  <ChevronDown className={`w-5 h-5 shrink-0 text-[#525252] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </div>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-[#525252] leading-relaxed">{f.a}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 px-6 bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-6xl font-heading font-bold tracking-tight leading-[0.95] mb-6">
            Make connections<br />that <span className="text-[#B8935A]">actually last.</span>
          </h2>
          <p className="text-lg text-white/70 max-w-xl mx-auto mb-10 leading-relaxed">
            Get your Sol Digital card and start turning every handshake into a relationship worth
            following up.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={cta} className="bg-[#B8935A] hover:bg-[#a3824c] text-black rounded-full h-14 px-8 text-base font-semibold" data-testid="final-cta">
              Start your free profile <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button onClick={() => navigate('/pricing')} variant="outline" className="rounded-full h-14 px-8 text-base font-semibold border-white/20 bg-transparent text-white hover:bg-white/10">
              See pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// -------------- Step illustrations (CSS-only mockups) --------------
const StepIllustration = ({ idx }) => {
  if (idx === 0) {
    // Profile builder mockup
    return (
      <div className="relative">
        <div className="rounded-3xl bg-gradient-to-br from-[#EBE6DC] to-[#F5F1EA] p-6 sm:p-10 shadow-xl shadow-black/5">
          <div className="bg-white rounded-2xl border border-[#0A0A0A]/10 p-6 max-w-sm mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#B8935A] to-[#8b6f3f]" />
              <div className="flex-1">
                <div className="h-3 bg-[#0A0A0A]/80 rounded w-2/3 mb-2" />
                <div className="h-2.5 bg-[#0A0A0A]/20 rounded w-1/2" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="h-9 bg-[#F5F1EA] rounded-lg" />
              <div className="h-9 bg-[#F5F1EA] rounded-lg" />
              <div className="grid grid-cols-4 gap-2 pt-2">
                {[0, 1, 2, 3].map((k) => (
                  <div key={k} className="aspect-square rounded-lg bg-[#0A0A0A]" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (idx === 1) {
    // Share methods with QR
    return (
      <div className="relative">
        <div className="rounded-3xl bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] p-6 sm:p-10 aspect-square flex items-center justify-center">
          <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
            <div className="aspect-square rounded-2xl bg-white p-4 flex items-center justify-center">
              <div className="w-full h-full bg-[repeating-linear-gradient(45deg,#0A0A0A_0px,#0A0A0A_4px,transparent_4px,transparent_8px)] rounded" />
            </div>
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#B8935A] to-[#8b6f3f] flex flex-col items-center justify-center text-black">
              <CreditCard className="w-8 h-8 mb-2" />
              <p className="text-xs font-bold">TAP TO SHARE</p>
            </div>
            <div className="aspect-square rounded-2xl bg-white/10 flex items-center justify-center">
              <Wallet className="w-8 h-8 text-white" />
            </div>
            <div className="aspect-square rounded-2xl bg-white/10 flex items-center justify-center">
              <LinkIcon className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Analytics illustration
  return (
    <div className="relative">
      <div className="rounded-3xl bg-gradient-to-br from-[#EBE6DC] to-[#F5F1EA] p-6 sm:p-10 shadow-xl shadow-black/5">
        <div className="bg-white rounded-2xl border border-[#0A0A0A]/10 p-6">
          <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">Views this week</p>
          <p className="text-4xl font-heading font-bold text-[#0A0A0A] mb-4">2,847</p>
          <div className="flex items-end gap-1.5 h-24">
            {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t bg-[#0A0A0A]"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div>
              <p className="text-xs text-[#525252]">Leads</p>
              <p className="font-heading font-bold text-lg">183</p>
            </div>
            <div>
              <p className="text-xs text-[#525252]">Saved</p>
              <p className="font-heading font-bold text-lg">412</p>
            </div>
            <div>
              <p className="text-xs text-[#525252]">Trees</p>
              <p className="font-heading font-bold text-lg text-[#B8935A]">0.36</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
